import React, { memo, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { areEqual } from 'Utils/equalityChecks';

import type { JobProfitability } from '../types';
import { formatCurrency } from '../format';
import classes from '../jobCosting.module.css';

interface Props {
  rows: JobProfitability[];
}

const MAX_BARS = 10;

const REVENUE_COLOR = '#3b82f6';
const COST_COLOR = '#f59e0b';
const BUDGET_COLOR = '#8b5cf6';

const truncate = (name: string | null, len = 16): string => {
  const value = name || 'Untitled';
  return value.length > len ? `${value.slice(0, len)}…` : value;
};

/** Compact axis labels: $7.5k, $1.2M. */
const formatCompact = (value: number): string => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${value}`;
};

interface TooltipEntry {
  dataKey?: string | number;
  value?: number | string;
  color?: string;
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className={classes.chartTooltip}>
      <div className={classes.chartTooltipTitle}>{label}</div>
      {payload.map((p) => (
        <div key={String(p.dataKey)} className={classes.chartTooltipRow}>
          <span className={classes.chartTooltipDot} style={{ background: p.color }} />
          <span className={classes.chartTooltipKey}>{p.dataKey}</span>
          <strong>{formatCurrency(Number(p.value))}</strong>
        </div>
      ))}
    </div>
  );
}

/** Revenue vs total cost for the top jobs (by revenue) on the current page. */
function OverviewChart({ rows }: Props) {
  const data = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, MAX_BARS)
        .map((r) => ({
          name: truncate(r.name),
          Revenue: Math.round(r.revenue),
          Cost: Math.round(r.totalCost),
          // Budget bar only for jobs that have one set (0 → no visible bar).
          Budget: r.hasBudget ? Math.round(r.totalBudget) : 0,
        })),
    [rows]
  );

  if (data.length === 0) return null;

  return (
    <div className={classes.chartCard}>
      <div className={classes.chartTitle}>{`Revenue vs Budget vs Cost — top ${data.length} by revenue`}</div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 8 }} barGap={4} barCategoryGap="28%">
          <defs>
            <linearGradient id="jcRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={REVENUE_COLOR} stopOpacity={0.95} />
              <stop offset="100%" stopColor={REVENUE_COLOR} stopOpacity={0.65} />
            </linearGradient>
            <linearGradient id="jcCost" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COST_COLOR} stopOpacity={0.95} />
              <stop offset="100%" stopColor={COST_COLOR} stopOpacity={0.6} />
            </linearGradient>
            <linearGradient id="jcBudget" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BUDGET_COLOR} stopOpacity={0.9} />
              <stop offset="100%" stopColor={BUDGET_COLOR} stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 6" stroke="#eef2f7" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            interval={0}
            angle={-18}
            textAnchor="end"
            height={56}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tickFormatter={(v) => formatCompact(Number(v))}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            width={56}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,23,42,0.04)' }} />
          <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Bar dataKey="Revenue" fill="url(#jcRevenue)" radius={[6, 6, 0, 0]} maxBarSize={46} />
          <Bar dataKey="Budget" fill="url(#jcBudget)" radius={[6, 6, 0, 0]} maxBarSize={46} />
          <Bar dataKey="Cost" fill="url(#jcCost)" radius={[6, 6, 0, 0]} maxBarSize={46} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const OverviewChartMemo = memo(OverviewChart, areEqual);

export { OverviewChartMemo as OverviewChart };
